import { foldSearchText } from "../domain/search";
import type { IconPath, IconWeight } from "../scene/schema";

/**
 * Built-in icon library (Phosphor Icons, MIT). The catalog is generated by
 * `scripts/build-icons.mjs` and loaded on demand: a small index for the search
 * and one chunk per style with the path data. Icon elements copy their paths,
 * so documents never need the catalog to render.
 */

/** One layer of an icon in a chunk: path data, or path data with opacity (duotone). */
export type IconChunkLayer = string | [string, number];
/** Icon name → layers, for one style. */
export type IconChunk = Record<string, IconChunkLayer[]>;

/** Generated index: category names and `[name, category indexes, tags]` per icon. */
export interface IconIndexData {
  version: string;
  categories: string[];
  icons: [string, number[], string[]][];
}

export interface IconInfo {
  name: string;
  categories: string[];
  tags: string[];
}

/** A German search word (both spellings of umlauts) and its English terms. */
export interface GermanIconEntry {
  keys: string[];
  terms: string[];
}

export interface IconCatalog {
  /** Phosphor version the catalog was built from. */
  version: string;
  icons: IconInfo[];
  byName: ReadonlyMap<string, IconInfo>;
  categories: string[];
  /** German keywords (`german.ts`), loaded with the index. */
  german: GermanIconEntry[];
}

export const ICON_WEIGHT_LABELS: Record<IconWeight, string> = {
  regular: "Regular",
  bold: "Bold",
  fill: "Fill",
  duotone: "Duotone",
  light: "Light",
  thin: "Thin",
};

export const ICON_WEIGHT_TITLES: Record<IconWeight, string> = {
  regular: "Normale Linien",
  bold: "Kräftige Linien",
  fill: "Gefüllt",
  duotone: "Linien mit heller Füllung",
  light: "Leichte Linien",
  thin: "Dünne Linien",
};

/** Line width of a style on the 256 grid (fill has no lines). */
export const ICON_STROKE_UNITS: Record<IconWeight, number | null> = {
  thin: 8,
  light: 12,
  regular: 16,
  duotone: 16,
  bold: 24,
  fill: null,
};

export const ICON_CATEGORY_LABELS: Record<string, string> = {
  arrows: "Pfeile",
  brands: "Marken & Social Media",
  commerce: "Handel",
  communications: "Kommunikation",
  design: "Design",
  editor: "Text & Bearbeiten",
  finances: "Finanzen",
  games: "Spiele",
  "health & wellness": "Gesundheit",
  "maps & travel": "Karten & Reisen",
  media: "Medien",
  nature: "Natur",
  objects: "Gegenstände",
  office: "Büro",
  people: "Personen",
  system: "System",
  "technology & development": "Technik",
  weather: "Wetter",
};

/** Shown while the search is empty. */
export const POPULAR_ICONS: readonly string[] = [
  "heart",
  "star",
  "check-circle",
  "check",
  "x",
  "plus",
  "arrow-right",
  "arrow-left",
  "arrow-up",
  "arrow-down",
  "caret-right",
  "phone",
  "envelope",
  "map-pin",
  "house",
  "user",
  "users",
  "calendar",
  "clock",
  "shopping-cart",
  "shopping-bag",
  "tag",
  "gift",
  "magnifying-glass",
  "bell",
  "chat-circle",
  "thumbs-up",
  "smiley",
  "lightbulb",
  "camera",
  "image",
  "play-circle",
  "music-notes",
  "globe",
  "link",
  "share-network",
  "instagram-logo",
  "facebook-logo",
  "youtube-logo",
  "tiktok-logo",
  "linkedin-logo",
  "whatsapp-logo",
  "x-logo",
  "leaf",
  "sun",
  "coffee",
  "fork-knife",
  "car",
  "airplane",
  "trophy",
  "sparkle",
  "fire",
  "lightning",
  "warning",
  "info",
  "question",
  "lock",
  "shield-check",
  "wifi-high",
  "percent",
  "currency-eur",
  "credit-card",
  "truck",
  "package",
];

// ---------------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------------

const chunkLoaders: Record<IconWeight, () => Promise<{ default: IconChunk }>> = {
  regular: () => import("./generated/regular"),
  bold: () => import("./generated/bold"),
  fill: () => import("./generated/fill"),
  duotone: () => import("./generated/duotone"),
  light: () => import("./generated/light"),
  thin: () => import("./generated/thin"),
};

let catalogPromise: Promise<IconCatalog> | null = null;
const chunkPromises = new Map<IconWeight, Promise<IconChunk>>();

/**
 * Names, categories, tags and German keywords of all icons (cached; a failed
 * load is retried next time).
 */
export function loadIconCatalog(): Promise<IconCatalog> {
  catalogPromise ??= Promise.all([import("./generated/index"), import("./german")]).then(
    ([{ default: data }, { GERMAN_ICON_TERMS }]) => {
      const icons = data.icons.map(([name, categories, tags]) => ({
        name,
        categories: categories.map((index) => data.categories[index]!),
        tags,
      }));
      return {
        version: data.version,
        icons,
        byName: new Map(icons.map((icon) => [icon.name, icon])),
        categories: data.categories,
        german: Object.entries(GERMAN_ICON_TERMS).map(([key, value]) => ({
          keys: spellings(key),
          terms: value.split(/\s+/).filter(Boolean),
        })),
      };
    },
    (error: unknown) => {
      catalogPromise = null;
      throw error;
    },
  );
  return catalogPromise;
}

/** Path data of all icons in one style (cached). */
export function loadIconWeight(weight: IconWeight): Promise<IconChunk> {
  let promise = chunkPromises.get(weight);
  if (!promise) {
    const loader = chunkLoaders[weight];
    if (!loader) return Promise.reject(new Error(`Unbekannter Icon-Stil: ${weight}`));
    promise = loader().then(
      (module) => module.default,
      (error: unknown) => {
        chunkPromises.delete(weight);
        throw error;
      },
    );
    chunkPromises.set(weight, promise);
  }
  return promise;
}

/** The layers of an icon as element paths, or null for an unknown name. */
export function iconLayers(chunk: IconChunk, name: string): IconPath[] | null {
  const layers = Object.hasOwn(chunk, name) ? chunk[name] : undefined;
  if (!layers) return null;
  return layers.map((layer) =>
    typeof layer === "string" ? { d: layer } : { d: layer[0], opacity: layer[1] },
  );
}

export async function loadIconPaths(name: string, weight: IconWeight): Promise<IconPath[] | null> {
  return iconLayers(await loadIconWeight(weight), name);
}

/** Readable name: "shopping-cart" → "Shopping cart". */
export function iconLabel(name: string): string {
  const words = name.replace(/-logo$/, "").split("-");
  const text = words.join(" ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// ---------------------------------------------------------------------------
// Search (English names and tags, German keywords)
// ---------------------------------------------------------------------------

/** A word folded like the list search does, plus the plain spelling ("hakchen"). */
function spellings(word: string): string[] {
  const folded = foldSearchText(word);
  const plain = word.toLowerCase().replace(/ß/g, "ss").normalize("NFKD").replace(/[̀-ͯ]/g, "");
  return folded === plain ? [folded] : [folded, plain];
}

/**
 * English terms for a German word with a weight: exact keys count fully,
 * keys the word starts with (compounds like "Herzchen") a little less and
 * keys starting with the word (while typing, from 3 letters) half.
 */
function weightedGermanTerms(german: GermanIconEntry[], word: string): Map<string, number> {
  const forms = spellings(word.trim());
  const terms = new Map<string, number>();
  for (const entry of german) {
    let weight = 0;
    for (const key of entry.keys) {
      for (const form of forms) {
        if (key === form) weight = Math.max(weight, 1);
        else if (key.length >= 4 && form.startsWith(key)) weight = Math.max(weight, 0.8);
        else if (form.length >= 3 && key.startsWith(form)) weight = Math.max(weight, 0.5);
      }
    }
    if (weight === 0) continue;
    for (const term of entry.terms) terms.set(term, Math.max(terms.get(term) ?? 0, weight));
  }
  return terms;
}

/** English terms for a German word (exact, compound or while typing). */
export function germanIconTerms(catalog: IconCatalog, word: string): string[] {
  return [...weightedGermanTerms(catalog.german, word).keys()];
}

interface PreparedIcon {
  info: IconInfo;
  words: string[];
}

const prepared = new WeakMap<IconCatalog, PreparedIcon[]>();
function prepare(catalog: IconCatalog): PreparedIcon[] {
  let list = prepared.get(catalog);
  if (!list) {
    list = catalog.icons.map((info) => ({ info, words: info.name.split("-") }));
    prepared.set(catalog, list);
  }
  return list;
}

/** How well one term matches an icon (0 = not at all). */
function termScore(icon: PreparedIcon, term: string, typed: boolean): number {
  const { name, tags } = icon.info;
  if (name === term) return 10;
  if (term.includes("-")) return name.startsWith(`${term}-`) ? 6 : 0;
  if (icon.words.includes(term)) return icon.words[0] === term ? 8 : 5;
  if (tags.includes(term)) return 3;
  // Prefixes only for what was typed (German terms are whole words).
  if (typed && term.length >= 2) {
    if (name.startsWith(term)) return 4;
    if (icon.words.some((word) => word.startsWith(term))) return 2;
    if (term.length >= 3 && tags.some((tag) => tag.startsWith(term))) return 1;
  } else if (!typed && icon.words.some((word) => word.startsWith(term))) {
    return 2;
  }
  return 0;
}

export interface IconSearchOptions {
  /** Only icons of this category (see `IconCatalog.categories`). */
  category?: string | undefined;
  limit?: number | undefined;
}

/**
 * Icons matching the query, best first. Every word has to match (English
 * names/tags or a German keyword); an empty query lists the category or the
 * popular icons.
 */
export function searchIcons(
  catalog: IconCatalog,
  query: string,
  options: IconSearchOptions = {},
): IconInfo[] {
  const limit = options.limit ?? Number.POSITIVE_INFINITY;
  const icons = prepare(catalog).filter(
    (icon) => !options.category || icon.info.categories.includes(options.category),
  );
  const text = query.trim();
  if (!text) {
    if (options.category) return icons.slice(0, limit).map((icon) => icon.info);
    return POPULAR_ICONS.flatMap((name) => catalog.byName.get(name) ?? []).slice(0, limit);
  }
  // A German phrase as a whole ("erste Hilfe"), otherwise word by word.
  const phrase = foldSearchText(text).replace(/\s+/g, " ");
  const isPhrase = catalog.german.some((entry) => entry.keys.includes(phrase));
  const groups = (isPhrase ? [text] : text.split(/\s+/)).map((word) => ({
    typed: spellings(word).flatMap((form) => [form, form.replace(/\s+/g, "-")]),
    german: weightedGermanTerms(catalog.german, word),
  }));
  const popularity = new Map(POPULAR_ICONS.map((name, index) => [name, index]));
  const scored: { info: IconInfo; score: number }[] = [];
  for (const icon of icons) {
    let total = 0;
    for (const group of groups) {
      let best = 0;
      for (const term of group.typed) best = Math.max(best, termScore(icon, term, true));
      for (const [term, weight] of group.german) {
        best = Math.max(best, termScore(icon, term, false) * weight);
      }
      if (best === 0) {
        total = 0;
        break;
      }
      total += best;
    }
    if (total > 0) scored.push({ info: icon.info, score: total });
  }
  scored.sort(
    (a, b) =>
      b.score - a.score ||
      (popularity.get(a.info.name) ?? 999) - (popularity.get(b.info.name) ?? 999) ||
      a.info.name.length - b.info.name.length ||
      a.info.name.localeCompare(b.info.name),
  );
  return scored.slice(0, limit).map((entry) => entry.info);
}
